import React, { memo, useMemo } from 'react';
import { useApi } from 'hooks/useApi';
import styles from './NewsSection.module.css';

// Constants moved outside component to prevent recreation on each render
const CONTACT_INFO = [
  {
    type: 'email',
    icon: '/Assets/Images/News/message.svg',
    value: 'info@axldrone.com',
    href: 'mailto:info@axldrone.com'
  },
  {
    type: 'phone', 
    icon: '/Assets/Images/News/phone.svg',
    value: '+91 484 299 3399',
    href: 'tel:+914842993399'
  }
];

// Memoized media logo component
const MediaLogo = memo(({ src, title, index, link,slug }) => (
  <a 
  key={index}
  href={link} 
  target="_blank" 
  rel="noopener noreferrer"
>
  <img 
    key={index}
    src={src}
    alt={title || `Media partner ${index + 1}`}
    className={styles.mediaLogo}
    loading="lazy"
    decoding="async"
  />
  </a>
));

MediaLogo.displayName = 'MediaLogo';

// Memoized contact item component
const ContactItem = memo(({ item }) => (
  <a 
    href={item.href}
    className={styles.contactItem}
    aria-label={`Contact via ${item.type}`}
  >
    <img 
      src={item.icon}
      alt=""
      className={styles.contactIcon}
      loading="lazy"
      decoding="async"
    />
    <p>{item.value}</p>
  </a>
));

ContactItem.displayName = 'ContactItem';

const NewsSection = memo(() => {
  // Environment variable for CMS base URL
  const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

  // Use the useApi hook
  const { data: newsData, loading, error } = useApi('/news');

  // Transform news data for media logos
  const mediaLogos = useMemo(() => {
    // Only return logos if API data is available and successful
    if (newsData?.success && newsData?.news_data && newsData.news_data.length > 0) {
      return newsData.news_data.map((newsItem, index) => (
        <MediaLogo 
          key={newsItem.id || index}
          src={`${CMS_BASE_URL}/attachments/${newsItem.logo}`}
          title={newsItem.title}
          index={index}
          link={newsItem.link}
          slug={newsItem.slug}
        />
      ));
    }
    // Return empty array if no data
    return [];
  }, [newsData, CMS_BASE_URL]);

  // Memoize contact items
  const contactItems = useMemo(() => 
    CONTACT_INFO.map((item, index) => (
      <ContactItem key={index} item={item} />
    )), []
  );

  // Check if we have valid news data
  const hasNewsData = newsData?.success && newsData?.news_data && newsData.news_data.length > 0;

  if (loading) {
    return (
      <section className={styles.newsContainer} aria-labelledby="news-title">
        <h2 id="news-title" className={styles.bannerTitle}>
          We are in the news!
        </h2>
        <div className={styles.banner} role="img" aria-label="News banner">
          <img 
            src="/Assets/Images/News/newsheader.svg"
            alt="News coverage banner"
            className={styles.bannerImage}
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
        </div>
        <div className={styles.content}>
          <div className={styles.loadingState}>
            <p>Loading news data...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    console.error('NewsSection API error:', error);
  }

  return (
    <section className={styles.newsContainer} aria-labelledby="news-title">
      <h2 id="news-title" className={styles.bannerTitle}>
        We are in the news!
      </h2>

      <div className={styles.banner} role="img" aria-label="News banner">
        <img 
          src="/Assets/Images/News/newsheader.svg"
          alt="News coverage banner"
          className={styles.bannerImage}
          loading="eager"
          decoding="async"
          fetchpriority="high"
        />
      </div>
      
      <div className={styles.content}>
        <header className={styles.wrapper}>
          <h3 className={styles.title}>Recent press</h3>
          <p className={styles.subtitle}>
            Here are some of our recent features and mentions in the media
          </p>
        </header>
        
        {hasNewsData ? (
          <div className={styles.mediaGrid} role="list" aria-label="Media partners">
            {mediaLogos}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p>No media coverage data available at the moment.</p>
          </div>
        )}

        <hr className={styles.divider} aria-hidden="true" />
        
        <section className={styles.contact} aria-labelledby="contact-title">
          <h3 id="contact-title" className={styles.contactTitle}>
            For media enquiries contact
          </h3>
          <div className={styles.contactInfo}>
            {contactItems}
          </div>
        </section>
      </div>
    </section>
  );
});

NewsSection.displayName = 'NewsSection';

export default NewsSection;